import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { statusInfo, STATUS_ORDER, GAUGE_ORDER, EXCEL_STATUSES, APP_STATUSES } from "@/constants/statuses";

function statusIcon(statut) {
  const c = statusInfo(statut).color;
  return L.divIcon({
    className: "",
    html: `<span style="display:block;width:16px;height:16px;border-radius:50%;background:${c};box-shadow:0 0 0 3px ${c}40,0 1px 5px rgba(0,0,0,.6);border:2px solid #0b0f17"></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

function esc(s) {
  return String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
}

function popCol(title, keys, counts) {
  const rows = keys
    .map((k) => {
      const info = statusInfo(k);
      return `<div class="sig-pop-row"><span class="sig-dot" style="background:${info.color}"></span>${info.label}<b>${counts[k] || 0}</b></div>`;
    })
    .join("");
  return `<div class="sig-pop-col"><div class="sig-pop-coltitle">${title}</div>${rows}</div>`;
}

function clusterPopupHtml(kids) {
  const cEx = {};
  const cApp = {};
  const streetFreq = {};
  let npa = "";
  let lieu = "";
  kids.forEach((m) => {
    cEx[m.statut] = (cEx[m.statut] || 0) + 1;
    if (m.statut_app) cApp[m.statut_app] = (cApp[m.statut_app] || 0) + 1;
    const st = [m.rue, m.numero].filter(Boolean).join(" ");
    if (st) streetFreq[st] = (streetFreq[st] || 0) + 1;
    if (m.code_postal) npa = m.code_postal;
    if (m.lieu) lieu = m.lieu;
  });
  const top = Object.entries(streetFreq).sort((a, b) => b[1] - a[1])[0];
  const addr = top ? top[0] : `${npa} ${lieu}`;
  return `<div class="sig-pop">
    <div class="sig-pop-title">${esc(addr)}</div>
    <div class="sig-pop-sub">${esc(npa)} ${esc(lieu)} · ${kids.length} compteurs</div>
    <div class="sig-pop-cols">
      ${popCol("Statuts Excel", EXCEL_STATUSES, cEx)}
      ${popCol("Statuts app", APP_STATUSES, cApp)}
    </div>
  </div>`;
}

function meterPopupHtml(m) {
  const ex = statusInfo(m.statut);
  const app = m.statut_app ? statusInfo(m.statut_app) : null;
  return `<div class="sig-pop">
    <div class="sig-pop-title">${esc([m.rue, m.numero].filter(Boolean).join(" ")) || "—"}</div>
    <div class="sig-pop-sub">${esc(m.code_postal)} ${esc(m.lieu)}</div>
    ${m.nom_famille ? `<div class="sig-pop-name">${esc(m.nom_famille)}</div>` : ""}
    <div class="sig-pop-row"><span class="sig-dot" style="background:${ex.color}"></span>${ex.label}</div>
    ${app ? `<div class="sig-pop-row"><span class="sig-dot" style="background:${app.color}"></span>${app.label}</div>` : ""}
    <div class="sig-pop-serial">ID ${esc(m.ref || m.numero_serie) || "—"}</div>
  </div>`;
}

function ClusterLayer({ meters, onSelect, fit }) {
  const map = useMap();
  const hasFitRef = useRef(false);

  useEffect(() => {
    const mcg = L.markerClusterGroup({
      chunkedLoading: true,
      removeOutsideVisibleBounds: true,
      maxClusterRadius: 55,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: false, // no auto-zoom into small points
      spiderfyOnMaxZoom: false, // no explosion into individual points
      iconCreateFunction: (cluster) => {
        const kids = cluster.getAllChildMarkers();
        const total = kids.length;
        const cEx = {};
        const cApp = {};
        kids.forEach((k) => {
          const s = k.meterData?.statut || "a_prendre_en_charge";
          cEx[s] = (cEx[s] || 0) + 1;
          const sa = k.meterData?.statut_app;
          if (sa) cApp[sa] = (cApp[sa] || 0) + 1;
        });
        const size = total < 10 ? 40 : total < 50 ? 52 : 64;
        // Each half fills bottom->top proportionally to its own group's counts.
        const half = (keys, counts) => {
          const order = GAUGE_ORDER.filter((k) => keys.includes(k));
          const groupTotal = order.reduce((a, k) => a + (counts[k] || 0), 0);
          if (!groupTotal) return `<div style="height:100%;background:#1E293B"></div>`;
          return order
            .map((k) => `<div style="height:${((counts[k] || 0) / groupTotal) * 100}%;background:${statusInfo(k).color}"></div>`)
            .join("");
        };
        return L.divIcon({
          className: "",
          html: `<div class="sig-cluster" style="width:${size}px;height:${size}px"><div class="sig-half sig-half-left">${half(EXCEL_STATUSES, cEx)}</div><div class="sig-half sig-half-right">${half(APP_STATUSES, cApp)}</div><span class="sig-cnt">${total}</span></div>`,
          iconSize: [size, size],
        });
      },
    });

    meters.forEach((m) => {
      if (m.lat == null || m.lng == null) return;
      const mk = L.marker([m.lat, m.lng], { icon: statusIcon(m.statut) });
      mk.meterData = m;
      mk.bindPopup(() => meterPopupHtml(m), { className: "sig-leaflet-popup", closeButton: true });
      mk.on("click", () => onSelect([m]));
      mcg.addLayer(mk);
    });

    mcg.on("clusterclick", (e) => {
      const kids = e.layer.getAllChildMarkers().map((k) => k.meterData);
      onSelect(kids);
      L.popup({ className: "sig-leaflet-popup", maxWidth: 320 })
        .setLatLng(e.latlng)
        .setContent(clusterPopupHtml(kids))
        .openOn(map);    });

    map.addLayer(mcg);

    if (fit && !hasFitRef.current) {
      const pts = meters.filter((m) => m.lat != null).map((m) => [m.lat, m.lng]);
      if (pts.length) {
        try {
          map.fitBounds(pts, { padding: [50, 50], maxZoom: 15 });
          hasFitRef.current = true;
        } catch (e) {}
      }
    }

    return () => {
      map.removeLayer(mcg);
    };
  }, [meters]); // eslint-disable-line

  return null;
}

export default function MapView({ meters, onSelect }) {
  return (
    <MapContainer center={[46.204391, 6.143158]} zoom={13} zoomControl={true} className="h-full w-full" data-testid="map-container">
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" maxZoom={19} />
      <ClusterLayer meters={meters} onSelect={onSelect} fit={true} />
    </MapContainer>
  );
}
